import { createEffect, untrack } from "solid-js";
import { fromActorRef } from "@fleet/state-machines/solid";
import { sessionActor } from "@/lib/session-machine";
import { useQuery } from "@tanstack/solid-query";
import { queryKeys } from "@folio/shared";
import { cloneSession } from "@/lib/auth";
import { api } from "@/lib/api";
import { SESSION_STALE_TIME_MS } from "@/lib/constants";

interface AppError {
  message?: string;
  status?: number;
}

function isAppError(error: unknown): error is AppError {
  return typeof error === "object" && error !== null && ("message" in error || "status" in error);
}

/**
 * Singleton component that synchronizes the TanStack Query session state
 * with the XState session actor.
 *
 * This MUST only be rendered once in the app (usually in AppProviders)
 * to prevent infinite reactivity loops.
 */
export function SessionBridge() {
  const snapshot = fromActorRef(sessionActor);

  const query = useQuery(() => ({
    queryKey: queryKeys.auth.session(),
    queryFn: async () => {
      const result = await api.GET("/api/auth/get-session", {
        params: { query: { jwt: true } },
      });
      return result.data ?? null;
    },
    // Only fetch if the machine is done priming (local storage check)
    enabled: !untrack(() => snapshot().value === "priming"),
    staleTime: SESSION_STALE_TIME_MS,
  }));

  createEffect(() => {
    // 1. Only sync if the query has SUCCEEDED.
    //
    // ⚠️ P23 / CC-60 · `isSuccess`, not `!isPending`, and early exit is
    // DELIBERATE. Before P23 this line read `query.data` without a guard: in
    // ERROR state the solid-query proxy SUSPENDED on read and ABORTED the
    // effect, so a 401 (or unresponsive server) never reached the machine
    // and the session primed from `localStorage` survived. Guarding only the
    // READ (`isSuccess && query.data`) removes that suspension and lets a
    // `SERVER_REPLY(session: null)` pass through → CLEARS the user account
    // whenever the server fails. Here that abort is reproduced. What confirms
    // guest mode is `runSessionCheck` in `beforeLoad` of `_app/route.tsx`,
    // not this bridge.
    if (query.isSuccess) {
      const sessionData = query.data ? cloneSession(query.data) : null;

      // 2. Prevent unnecessary updates using robust manual comparison.
      // Use untrack to avoid dependency on the machine state while reading it.
      const current = untrack(() => snapshot().context.session);
      const currentErr = untrack(() => {
        const err = snapshot().context.error;
        return isAppError(err) ? err : null;
      });

      const isDataSame =
        (!sessionData && !current) || (sessionData && sessionData.user.id === current?.user.id);

      // Under `isSuccess` the query has NO error (`query.error` is `null`, and the
      // type narrows to `never`): this bridge only propagates sessions
      // CONFIRMED by the server. The only thing left to check is that the
      // machine does not carry an old error — in which case we do need to send,
      // to clear it.
      const isErrorSame = currentErr === null;

      if (isDataSame && isErrorSame) {
        return;
      }

      console.log("[SessionBridge] Syncing session to machine:", {
        hasData: Boolean(sessionData),
        hasError: false,
      });

      sessionActor.send({
        type: "SERVER_REPLY",
        session: sessionData,
        error: null,
      });
    }
  });

  return null;
}
